<template>
    <div class="bg-white overflow-hidden shadow rounded-lg divide-y divide-gray-200">
        <div class="-ml-4 -mt-2 flex items-center justify-between flex-wrap sm:flex-nowrap px-4 py-5 sm:p-6">
            <div class="ml-4 mt-2">
                <h3 class="text-lg leading-6 font-medium text-gray-900">General Item Information</h3>
            </div>
        </div>

        <div class="px-4 py-5 sm:p-6">

            <Fieldset heading="About the item">
                <FormField :field="state.asset_form.fields.public_headline" name="public_headline" namespace="asset" :disabled="state.asset_form.fields.public_headline.disabled" class="mb-4" />
                <FormField :field="state.asset_form.fields.public_body" name="public_body" namespace="asset" :disabled="state.asset_form.fields.public_body.disabled" class="mb-4" />
                <FormField :field="state.asset_form.fields.auctioneer_quote" name="auctioneer_quote" namespace="asset" :disabled="state.asset_form.fields.auctioneer_quote.disabled" />
            </Fieldset>

            <Fieldset heading="Item grade">
                <FormField :field="state.asset_form.fields.auctioneer_grade" name="auctioneer_grade" namespace="asset" :disabled="state.asset_form.fields.auctioneer_grade.disabled" class="mb-4" />
                <FormField :field="state.asset_form.fields.auctioneer_grade_notes" name="auctioneer_grade_notes" namespace="asset" :disabled="state.asset_form.fields.auctioneer_grade_notes.disabled" />
            </Fieldset>

            <Fieldset v-if="asset_data" heading="YouTube Video">
                <FormField :field="state.media_form.fields.url" name="url" namespace="media" :disabled="state.media_form.fields.url.disabled" />
            </Fieldset>

        </div>

        <div class="px-4 py-4 sm:px-6 text-right">
            <Button type="button" width="w-auto" class="ml-2" :onClick="() => submitStep()" :disabled="!next_button_active">Next</Button>
        </div>

    </div>
</template>

<script>
import { useAssetStore } from '@sto/asset.store'
import { useSystemStore } from '@sto/system.store'
import { useAuthStore } from '@sto/auth.store'

import { computed, onMounted, onBeforeMount, reactive, watch } from 'vue'
import { isValid, getPayload, getUserOrganisationType } from '@lib/helpers'
import { useRoute } from 'vue-router'
import config from '@/config'

import LoadingIcon from '@ico/LoadingIcon.vue'
import Fieldset from "@glo/Fieldset.vue";

export default {
    components: {
        Fieldset,
        LoadingIcon
    },
    setup() {
        const ROUTE = useRoute()
        const ASSET_STORE = useAssetStore()
        const SYSTEM_STORE = useSystemStore()
        const AUTH_STORE = useAuthStore()

        const asset_id = computed(() => {
            return ASSET_STORE?.asset?.id || null
        })

        const asset_data = computed(() => {
            return ASSET_STORE?.asset || null
        })

        const next_button_active = computed(() => {

            let active = true

            for (let f in state.asset_form.fields) {
                if (state.asset_form.fields[f].required && !state.asset_form.fields[f].value) active = false
            }

            return active
        })

        const state = reactive({
            asset_form: {
                show_fields: false,
                valid: false,
                has_changes: computed(() => {
                    let has_changes = false
                    for (let f in state.asset_form.fields) {
                        if (state.asset_form.fields[f].value != state.asset_form.fields[f].stored_value) has_changes = true
                    }
                    return has_changes
                }),
                loading: false,
                fields: {
                    public_headline: {
                        active: true,
                        value: '',
                        stored_value: '',
                        label: 'Title',
                        type: 'text',
                        required: true,
                        pristine: true,
                        valid: true,
                        validation: [],
                        message: '',
                        notes: '',
                        disabled: false
                    },
                    public_body: {
                        active: true,
                        value: '',
                        stored_value: '',
                        label: 'Description',
                        type: 'richtext',
                        richtext_options: {
                            formatting: ['bold', 'italic', 'ul']
                        },
                        required: false,
                        pristine: true,
                        valid: true,
                        validation: [],
                        message: '',
                        notes: '',
                        disabled: false
                    },
                    auctioneer_grade: {
                        active: true,
                        label: 'Grade',
                        type: 'select',
                        value: '',
                        stored_value: '',
                        options: [
                            {
                                value: '',
                                label: 'Please choose...'
                            },
                            {
                                value: '',
                                label: 'Please choose...'
                            },
                            {
                                value: '1',
                                label: '1'
                            },
                            {
                                value: '1.5',
                                label: '1.5'
                            },
                            {
                                value: '2',
                                label: '2'
                            },
                            {
                                value: '2.5',
                                label: '2.5'
                            },
                            {
                                value: '3',
                                label: '3'
                            },
                            {
                                value: '3.5',
                                label: '3.5'
                            },
                            {
                                value: '4',
                                label: '4'
                            },
                            {
                                value: '4.5',
                                label: '4.5'
                            },
                            {
                                value: '5',
                                label: '5'
                            },
                            {
                                value: '1 (Trade)',
                                label: '1 (Trade)'
                            },
                            {
                                value: '1.5 (Trade)',
                                label: '1.5 (Trade)'
                            },
                            {
                                value: '2 (Trade)',
                                label: '2 (Trade)'
                            },
                            {
                                value: '2.5 (Trade)',
                                label: '2.5 (Trade)'
                            },
                            {
                                value: '3 (Trade)',
                                label: '3 (Trade)'
                            },
                            {
                                value: '3.5 (Trade)',
                                label: '3.5 (Trade)'
                            },
                            {
                                value: '4 (Trade)',
                                label: '4 (Trade)'
                            },
                            {
                                value: '4.5 (Trade)',
                                label: '4.5 (Trade)'
                            },
                            {
                                value: '5 (Trade)',
                                label: '5 (Trade)'
                            },
                            {
                                value: 'Classic',
                                label: 'Classic'
                            },
                            {
                                value: 'Unclassified',
                                label: 'Unclassified'
                            }
                        ],
                        required: false,
                        pristine: true,
                        valid: true,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: false,
                        disabled: false
                    },
                    auctioneer_grade_notes: {
                        active: true,
                        label: 'Grade notes',
                        value: '',
                        stored_value: '',
                        type: 'richtext',
                        richtext_options: {
                            formatting: ['bold', 'italic', 'ul']
                        },
                        required: false,
                        pristine: true,
                        valid: true,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: false,
                        disabled: false
                    },
                    auctioneer_quote: {
                        active: true,
                        value: '',
                        label: 'Auctioneer quote',
                        type: 'richtext',
                        richtext_options: {
                            formatting: ['bold', 'italic', 'ul']
                        },
                        required: false,
                        pristine: true,
                        valid: true,
                        validation: [],
                        message: '',
                        notes: '',
                        disabled: false
                    },
                    status: {
                        active: true,
                        value: 'ready',
                        label: '',
                        type: 'hidden',
                        required: false,
                        pristine: true,
                        valid: true,
                        validation: [],
                        message: '',
                        notes: '',
                        disabled: false
                    },
                }
            },
            media_form: {
                loading: false,
                fields: {
                    url: {
                        active: true,
                        label: 'YouTube Share URL',
                        type: 'text',
                        value: null,
                        stored_value: null,
                        required: false,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: false,
                        disabled: false
                    }
                }
            }
        })

        const setAssetData = asset => {
            if (!asset) return

            for (let field in state.asset_form.fields) {
                if (asset[field]) {
                    state.asset_form.fields[field].value = asset[field]
                    state.asset_form.fields[field].stored_value = asset[field]
                }
                state.asset_form.fields[field].pristine = false
            }

            let media = Array.isArray(asset.media)
                ? asset.media
                : []

            media.sort(function(a, b) {
                return a.sort_order - b.sort_order;
            });

            const youtube_embed = media.find(m => m.type === 'youtube-embed')

            if (youtube_embed) {
                state.media_form.fields.url.value = youtube_embed.url
                state.media_form.fields.url.stored_value = youtube_embed.url
                state.media_form.fields.url.pristine = false
                state.media_form.fields.url.valid = true
            }
        }

        const submitStep = async () => {

            // fields will automatically create their own validation states
            if (state.asset_form.loading || !isValid(state.asset_form.fields)) return

            // if the value is present and unchaned, just skip to the next step
            if (!state.asset_form.has_changes) {
                ASSET_STORE.activateNextStep()
                return
            }

            state.asset_form.loading = true

            try {

                let promises = []

                if (state.media_form.fields.url.value) {
                    promises.push(saveAssetMedia())
                }

                promises.push(saveAsset())

                await Promise.all(promises)

                ASSET_STORE.activateNextStep()

            } catch (error) {
                console.error('submitStep', error)
                SYSTEM_STORE.addError('Error', error?.message || 'There was a problem saving your data')

            } finally {
                state.asset_form.loading = false
            }
        }

        const saveAssetMedia = async () => {
            const payload = getPayload(state.media_form.fields)
            await ASSET_STORE.addReplaceYouTube(asset_id.value, payload)
        }

        const saveAsset = () => {
            const asset_payload = getPayload(state.asset_form.fields)
            return ASSET_STORE.update(ASSET_STORE.asset.id, asset_payload)
        }

        const refreshAsset = async () => {
            if (!asset_id.value) return
            return ASSET_STORE.showAndSetStore(asset_id.value)
        }

        watch(
            () => asset_data.value,
            (new_value, old_value) => {
                setAssetData(asset_data.value)
            }
        )

        onMounted(() => {

        })

        return {
            ASSET_STORE,
            state,
            asset_id,
            asset_data,
            next_button_active,
            submitStep,
            refreshAsset
        }
    }
}
</script>

